// Pricing.js
import React, { useState } from 'react';
import './Pages/PagesCss/Pricing.css';
import { useNavigate } from 'react-router-dom';

// ─── Icons ────────────────────────────────────────────────────────────────────
const Check = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7.5" stroke="currentColor" strokeOpacity=".2"/>
    <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const Dash = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7.5" stroke="#CBD5E1" strokeOpacity=".5"/>
    <path d="M5 8H11" stroke="#CBD5E1" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const Zap = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M13 2L4.5 13.5H12L11 22L19.5 10.5H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
  </svg>
);
const Shield = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M12 3L3 7V12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12V7L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const Users = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 20C3 17.2386 5.68629 15 9 15C12.3137 15 15 17.2386 15 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 11C17.6569 11 19 9.65685 19 8C19 6.34315 17.6569 5 16 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M21 20C21 17.7909 19.2091 16 17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const Star = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
    <path d="M7 1L8.8 5.1L13.3 5.6L10.1 8.5L11 13L7 10.8L3 13L3.9 8.5L0.7 5.6L5.2 5.1L7 1Z"/>
  </svg>
);
const Infinity = () => (
  <svg width="20" height="14" viewBox="0 0 28 16" fill="none">
    <path d="M14 8C14 8 11 3 7 3C3.68629 3 1 5.68629 1 8C1 10.3137 3.68629 13 7 13C11 13 14 8 14 8ZM14 8C14 8 17 13 21 13C24.3137 13 27 10.3137 27 8C27 5.68629 24.3137 3 21 3C17 3 14 8 14 8Z" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

// ─── Plan data ────────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For occasional use',
    monthly: 0,
    yearly: 0,
    color: '#64748B',
    highlight: false,
    badge: null,
    cta: 'Get started free',
    ctaVariant: 'ghost',
    limits: {
      files: '2 files / day',
      size: '10 MB per file',
      batch: false,
      watermark: 'Watermark on output',
      speed: 'Standard speed',
      support: 'Community support',
      api: false,
      seats: '1 user',
      history: '7-day file history',
    },
    features: [
      { label: 'All 22 PDF tools',              included: true  },
      { label: '2 files processed per day',      included: true  },
      { label: 'Up to 10 MB per file',           included: true  },
      { label: 'Watermark-free output',          included: false },
      { label: 'Batch processing',               included: false },
      { label: 'Priority processing',            included: false },
      { label: 'File history (30 days)',         included: false },
      { label: 'API access',                     included: false },
      { label: 'Team seats',                     included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For power users & freelancers',
    monthly: 7.99,
    yearly: 5.99,
    color: '#3B82F6',
    highlight: true,
    badge: 'Most Popular',
    cta: 'Start Pro free 7 days',
    ctaVariant: 'primary',
    limits: {
      files: 'Unlimited files',
      size: '100 MB per file',
      batch: true,
      watermark: 'No watermarks',
      speed: 'Priority processing',
      support: 'Email support',
      api: false,
      seats: '1 user',
      history: '30-day file history',
    },
    features: [
      { label: 'All 22 PDF tools',              included: true },
      { label: 'Unlimited files per day',        included: true },
      { label: 'Up to 100 MB per file',          included: true },
      { label: 'Watermark-free output',          included: true },
      { label: 'Batch processing',               included: true },
      { label: 'Priority processing',            included: true },
      { label: 'File history (30 days)',         included: true },
      { label: 'API access',                     included: false },
      { label: 'Team seats',                     included: false },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    tagline: 'For teams & agencies',
    monthly: 19.99,
    yearly: 14.99,
    color: '#0F172A',
    highlight: false,
    badge: null,
    cta: 'Start Business free 14 days',
    ctaVariant: 'dark',
    limits: {
      files: 'Unlimited files',
      size: '500 MB per file',
      batch: true,
      watermark: 'No watermarks',
      speed: 'Dedicated processing',
      support: 'Priority support',
      api: true,
      seats: '3 users included',
      history: '90-day file history',
    },
    features: [
      { label: 'All 22 PDF tools',              included: true },
      { label: 'Unlimited files per day',        included: true },
      { label: 'Up to 500 MB per file',          included: true },
      { label: 'Watermark-free output',          included: true },
      { label: 'Batch processing',               included: true },
      { label: 'Dedicated processing queue',     included: true },
      { label: 'File history (90 days)',         included: true },
      { label: 'API access',                     included: true },
      { label: '3 team seats included',         included: true },
    ],
  },
];

const FAQS = [
  {
    q: 'Can I really use all 22 tools for free?',
    a: 'Yes every tool is available on the Free plan. The only limits are 2 files per day and a 10 MB file size cap. No credit card, no sign-up needed.',
  },
  {
    q: 'What happens when my free trial ends?',
    a: 'You automatically drop to the Free plan no charge, no interruption. We\'ll remind you 3 days before so you\'re never surprised.',
  },
  {
    q: 'Can I switch plans at any time?',
    a: 'Absolutely. Upgrade, downgrade or cancel whenever you want from your account settings. Downgrades take effect at the end of your billing cycle.',
  },
  {
    q: 'What does "batch processing" mean?',
    a: 'Batch lets you upload and process multiple files at once. For example: compress 20 PDFs in a single operation instead of one at a time.',
  },
  {
    q: 'Does the Business plan include more than 3 seats?',
    a: 'Yes additional seats can be added for $4.99/seat/month. Contact us if you need 10+ seats and we\'ll sort out a custom deal.',
  },
  {
    q: 'Is my data safe?',
    a: 'Files are processed in memory and permanently deleted from our servers within 1 hour of processing. We never read, store or share your documents.',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function Pricing() {
  const navigate = useNavigate();
  const [yearly, setYearly]   = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const price = (plan) =>
    plan.monthly === 0 ? 'Free' : `$${yearly ? plan.yearly : plan.monthly}`;

  const saving = (plan) => {
    if (plan.monthly === 0) return null;
    const saved = ((plan.monthly - plan.yearly) * 12).toFixed(0);
    return `Save $${saved}/yr`;
  };

  return (
    <div className="pr-page">

      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="pr-hero">
        <div className="pr-hero-bg" aria-hidden="true" />
        <div className="pr-container">

          <div className="pr-hero-badge">
            <span className="pr-badge-dot" />
            Simple, transparent pricing
          </div>

          <h1 className="pr-hero-title">
            The right plan for<br />
            <span className="pr-highlight">every workflow</span>
          </h1>
          <p className="pr-hero-sub">
            Start free. Upgrade when you need more. Cancel any time no questions asked.
          </p>

          {/* Billing toggle */}
          <div className="pr-toggle-wrap">
            <span className={`pr-toggle-label ${!yearly ? 'active' : ''}`}>Monthly</span>
            <button
              className={`pr-toggle${yearly ? ' on' : ''}`}
              onClick={() => setYearly(v => !v)}
              aria-label="Toggle yearly billing"
            >
              <span className="pr-toggle-thumb" />
            </button>
            <span className={`pr-toggle-label ${yearly ? 'active' : ''}`}>
              Yearly
              <span className="pr-toggle-save">Save up to 25%</span>
            </span>
          </div>
        </div>
      </section>

      {/* ── Plans ─────────────────────────────────────────── */}
      <section className="pr-plans-section">
        <div className="pr-container">
          <div className="pr-plans">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`pr-plan${plan.highlight ? ' pr-plan--highlight' : ''}`}
              >
                {/* Popular badge */}
                {plan.badge && (
                  <div className="pr-plan-badge">
                    <Star /> {plan.badge}
                  </div>
                )}

                {/* Plan header */}
                <div className="pr-plan-head">
                  <div className="pr-plan-name-row">
                    <span className="pr-plan-name" style={{ color: plan.highlight ? plan.color : 'inherit' }}>
                      {plan.name}
                    </span>
                  </div>
                  <p className="pr-plan-tagline">{plan.tagline}</p>

                  <div className="pr-plan-price-row">
                    <span className="pr-plan-price">{price(plan)}</span>
                    {plan.monthly > 0 && (
                      <span className="pr-plan-per">/ mo</span>
                    )}
                  </div>
                  {yearly && plan.monthly > 0 && (
                    <span className="pr-plan-saving">{saving(plan)}</span>
                  )}
                  {plan.monthly === 0 && (
                    <span className="pr-plan-forever">Forever free · No card needed</span>
                  )}
                </div>

                {/* CTA */}
                <button
                  className={`pr-cta pr-cta--${plan.ctaVariant}`}
                  onClick={() => plan.id === 'free' ? navigate('/PDFMerger') : navigate('/signup')}
                >
                  {plan.cta}
                  {plan.id !== 'free' && <ArrowRight />}
                </button>

                {/* Divider */}
                <div className="pr-plan-divider" />

                {/* Key limits row */}
                <div className="pr-limits">
                  <div className="pr-limit">
                    <Infinity />
                    <span>{plan.limits.files}</span>
                  </div>
                  <div className="pr-limit">
                    <Zap />
                    <span>{plan.limits.size}</span>
                  </div>
                  <div className="pr-limit">
                    <Shield />
                    <span>{plan.limits.watermark}</span>
                  </div>
                  <div className="pr-limit">
                    <Users />
                    <span>{plan.limits.seats}</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="pr-plan-divider" />

                {/* Feature list */}
                <ul className="pr-features">
                  {plan.features.map((f, i) => (
                    <li key={i} className={`pr-feature${f.included ? '' : ' pr-feature--off'}`}>
                      <span className="pr-feature-icon" style={{ color: f.included ? plan.color : undefined }}>
                        {f.included ? <Check /> : <Dash />}
                      </span>
                      {f.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison table ──────────────────────────────── */}
      <section className="pr-table-section">
        <div className="pr-container">
          <div className="pr-section-head">
            <span className="pr-section-label">Compare plans</span>
            <h2 className="pr-section-title">Everything side by side</h2>
          </div>

          <div className="pr-table-wrap">
            <table className="pr-table">
              <thead>
                <tr>
                  <th className="pr-th-feature">Feature</th>
                  <th>Free</th>
                  <th className="pr-th-highlight">Pro</th>
                  <th>Business</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['All 22 PDF tools',         '✓', '✓', '✓'],
                  ['Files per day',             '2', 'Unlimited', 'Unlimited'],
                  ['Max file size',             '10 MB', '100 MB', '500 MB'],
                  ['Watermark-free output',     '—', '✓', '✓'],
                  ['Batch processing',          '—', '✓', '✓'],
                  ['Processing speed',          'Standard', 'Priority', 'Dedicated'],
                  ['File history',              '7 days', '30 days', '90 days'],
                  ['API access',                '—', '—', '✓'],
                  ['Team seats',                '1', '1', '3 included'],
                  ['Support',                   'Community', 'Email', 'Priority email'],
                  ['Free trial',                '—', '7 days', '14 days'],
                ].map(([feat, free, pro, biz], i) => (
                  <tr key={i}>
                    <td className="pr-td-feature">{feat}</td>
                    <td className="pr-td">{free === '—' ? <span className="pr-td-dash">—</span> : free === '✓' ? <span className="pr-td-check">✓</span> : free}</td>
                    <td className="pr-td pr-td-highlight">{pro === '—' ? <span className="pr-td-dash">—</span> : pro === '✓' ? <span className="pr-td-check pr-td-check--pro">✓</span> : pro}</td>
                    <td className="pr-td">{biz === '—' ? <span className="pr-td-dash">—</span> : biz === '✓' ? <span className="pr-td-check">✓</span> : biz}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────── */}
      <section className="pr-faq-section">
        <div className="pr-container pr-container--narrow">
          <div className="pr-section-head">
            <span className="pr-section-label">FAQ</span>
            <h2 className="pr-section-title">Questions & answers</h2>
          </div>

          <div className="pr-faqs">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className={`pr-faq${openFaq === i ? ' open' : ''}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="pr-faq-q">
                  {faq.q}
                  <span className="pr-faq-chevron">{openFaq === i ? '−' : '+'}</span>
                </div>
                {openFaq === i && (
                  <div className="pr-faq-a">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────── */}
      <section className="pr-bottom-cta">
        <div className="pr-container">
          <h2 className="pr-cta-title">Start for free today</h2>
          <p className="pr-cta-sub">No credit card. No account. Just pick a tool and go.</p>
          <div className="pr-cta-btns">
            <button className="pr-cta pr-cta--primary pr-cta--large" onClick={() => navigate('/PDFMerger')}>
              Try a tool now <ArrowRight />
            </button>
            <button className="pr-cta pr-cta--ghost pr-cta--large" onClick={() => navigate('/PDFTools')}>
              Browse all tools
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}